import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { unwrapResult } from "@reduxjs/toolkit";
import Drawer from "../Drawer";
import { LoaderSpinner } from "../LoaderSpinner";
import { ConfrimModal } from "../ConfrimModal";
import { useDebounce } from "../../helpers/useDebounce";
import {
    getShipmentChangeLog,
    rollbackShipmentChanges,
    ShipmentsLoadingKeys,
} from "../../slices/shippingSlice";

const FIELD_LABELS = {
    box_received: "Received Qty",
    box_shipped: "Shipped Qty",
    shipped_quantity_is_manual: "Shipped Qty Manual",
    box_shipping_method: "Shipping Method",
    box_tracking: "Tracking #",
    box_shipdate: "Date Shipped",
    box_receive_date: "Est. Receive Date",
    box_actual_date: "Received Date",
    box_qty: "Ordered Qty",
    container_shipping_method: "Shipping Method",
    container_tracking: "Tracking #",
    container_shipdate: "Date Shipped",
    container_receive_date: "Est. Receive Date",
    container_actual_date: "Received Date",
    container_received: "Received Qty",
    container_shipped: "Shipped Qty",
    container_qty: "Ordered Qty",
    created: "Created",
    deleted: "Deleted",
};

const SOURCE_LABELS = {
    manual: "Manual edit",
    receive_all: "Receive all",
    bulk_edit: "Bulk edit",
    rollback: "Rollback",
    rfid_scan: "RFID scan",
    create: "Created",
    delete: "Deleted",
};

const formatWhen = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const formatValue = (value) => value == null || value === "" ? "—" : String(value);

const getItemLabel = (row) => {
    if (row.entityType === "box") return `Box #${row.entityId}`;
    if (row.entityType === "container_item") return row.sku || `Container item #${row.entityId}`;
    return row.entityName || `Container #${row.entityId}`;
};

export const ShipmentChangeHistoryDrawer = ({
    isOpen,
    onClose,
    marginFromSidebar,
    onChanged,
    refreshToken,
}) => {
    const dispatch = useDispatch();
    const {
        shipmentChangeLog,
        shipmentChangeLogPagination,
        loadingKeys,
    } = useSelector((state) => state.shipments);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pendingRollback, setPendingRollback] = useState(null);
    const search = useDebounce(searchTerm, 350);
    const limit = shipmentChangeLogPagination?.limit || 100;
    const total = shipmentChangeLogPagination?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const loading = loadingKeys.includes(ShipmentsLoadingKeys.getShipmentChangeLog);
    const rollingBack = loadingKeys.includes(ShipmentsLoadingKeys.rollbackShipmentChanges);

    const refreshLog = useCallback(() => {
        if (!isOpen) return;
        dispatch(getShipmentChangeLog({
            search,
            limit,
            page: currentPage,
        }));
    }, [currentPage, dispatch, isOpen, limit, search]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentPage(1);
            setSearchTerm("");
            setPendingRollback(null);
            return;
        }
        refreshLog();
    }, [isOpen, refreshLog, refreshToken]);

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const rows = useMemo(
        () => (Array.isArray(shipmentChangeLog) ? shipmentChangeLog : []),
        [shipmentChangeLog]
    );

    const confirmRollback = async () => {
        if (!pendingRollback) return;
        try {
            const result = await dispatch(rollbackShipmentChanges({
                changeLogId: pendingRollback.id,
            }));
            unwrapResult(result);
            setPendingRollback(null);
            onChanged?.();
            refreshLog();
        } catch {
            setPendingRollback(null);
        }
    };

    return (
        <>
            <Drawer
                isOpen={isOpen}
                onClose={onClose}
                width="1100px"
                marginFromSidebar={marginFromSidebar}
                showActionButtons={false}
                headerContent={
                    <div className="flex items-center justify-between gap-3 pr-2">
                        <h2 className="text-xl font-bold text-gray-900">Shipping Change History</h2>
                        <div className="flex items-center gap-1">
                            <span className="mr-2 text-xs text-gray-500">{total} changes</span>
                            <button
                                type="button"
                                disabled={currentPage <= 1 || loading}
                                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                className="rounded-md bg-white p-1.5 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ChevronLeftIcon className="h-3 w-3" />
                            </button>
                            <span className="min-w-[3rem] text-center text-xs text-gray-500">
                                {currentPage}/{totalPages}
                            </span>
                            <button
                                type="button"
                                disabled={currentPage >= totalPages || loading}
                                onClick={() => setCurrentPage((page) => page + 1)}
                                className="rounded-md bg-white p-1.5 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ChevronRightIcon className="h-3 w-3" />
                            </button>
                        </div>
                    </div>
                }
            >
                <div className="flex flex-col gap-3 px-4 pb-6">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search PO, SKU, field, or value"
                        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
                    />
                    {loading ? (
                        <div className="flex justify-center py-10">
                            <LoaderSpinner width="48" height="48" border="4" colorSpinner="gray" />
                        </div>
                    ) : !rows.length ? (
                        <p className="py-10 text-center text-sm text-gray-500">No shipping changes logged yet.</p>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-gray-50 text-gray-600">
                                    <tr>
                                        {["When", "PO #", "Item", "Field", "Change", "Source", "By", "Actions"].map((heading) => (
                                            <th key={heading} className="whitespace-nowrap px-3 py-2 font-medium">{heading}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const reverted = row.rolledBackChangeId || row.source === "rollback";
                                        const isLifecycle = row.field === "created" || row.field === "deleted";
                                        return (
                                            <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                                                <td className="whitespace-nowrap px-3 py-2 align-top text-gray-500">{formatWhen(row.createdAt)}</td>
                                                <td className="whitespace-nowrap px-3 py-2 align-top font-medium text-gray-800">{row.poNumber || row.poId || "—"}</td>
                                                <td className="whitespace-nowrap px-3 py-2 align-top text-gray-700">{getItemLabel(row)}</td>
                                                <td className="whitespace-nowrap px-3 py-2 align-top text-gray-700">{FIELD_LABELS[row.field] || row.field}</td>
                                                <td className="whitespace-nowrap px-3 py-2 align-top text-gray-700">
                                                    {isLifecycle
                                                        ? formatValue(row.newValue ?? row.oldValue)
                                                        : (
                                                            <>
                                                                {formatValue(row.oldValue)}
                                                                <span className="mx-1 text-gray-400">→</span>
                                                                {formatValue(row.newValue)}
                                                            </>
                                                        )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 align-top">
                                                    <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                                        {SOURCE_LABELS[row.source] || row.source || "Manual edit"}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 align-top text-gray-500">
                                                    {row.changedBy?.name || row.changedBy?.email || "—"}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-right">
                                                    {isLifecycle ? (
                                                        <span className="text-xs text-gray-400">—</span>
                                                    ) : reverted ? (
                                                        <span className="text-xs text-gray-400">Reverted</span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            disabled={rollingBack}
                                                            onClick={() => setPendingRollback(row)}
                                                            className="rounded-md border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            Revert
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Drawer>
            <ConfrimModal
                isOpen={Boolean(pendingRollback)}
                isLoading={rollingBack}
                title="Revert shipping change?"
                description={pendingRollback
                    ? `Restore ${FIELD_LABELS[pendingRollback.field] || pendingRollback.field} on ${getItemLabel(pendingRollback)} to ${formatValue(pendingRollback.oldValue)}?`
                    : ""}
                confirmButtonTitle="Revert"
                submitClick={confirmRollback}
                cancelClick={() => {
                    if (!rollingBack) setPendingRollback(null);
                }}
            />
        </>
    );
};

export default ShipmentChangeHistoryDrawer;
